import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { IconPlayerPlayFilled, IconSearch, IconSparkles } from "@tabler/icons-react"
import { useCallback, useMemo, useState } from "react"
import { formatClock, formatShortDate } from "../../../lib/format-date"
import { displayMeetingTitle } from "../../../lib/meeting-title"
import { ChannelLabel } from "../../channel-meta"
import { buildHighlightRanges, toSegments, windowSnippet } from "../../SearchHitCard/helpers"
import {
  buildPaletteRows,
  groupPaletteBlocks,
  PALETTE_MIN_QUERY,
  type TPaletteBlock,
  type TPaletteRow,
} from "../palette"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

const LIST_ID = "ask-palette-list"

function rowDomId(row: TPaletteRow): string {
  return `ask-palette-${row.id}`
}

function activateRow(row: TPaletteRow, handlers: Pick<IProps, "onAsk" | "onStarter" | "onOpenHit" | "onSeeAll">): void {
  switch (row.kind) {
    case "ask":
      handlers.onAsk(row.query)
      return
    case "starter":
      handlers.onStarter(row.label)
      return
    case "hit":
      handlers.onOpenHit(row.hit)
      return
    case "see-all":
      handlers.onSeeAll(row.query)
      return
  }
}

/**
 * One box for search and questions. Props in, events out: the host owns the
 * debounced search, the starter labels, and where Enter goes. Arrow keys move
 * the highlight; Enter opens it; Escape closes.
 */
export const AskOverlayView = ({
  query,
  onQueryChange,
  aiEnabled,
  hits,
  searching,
  searchError,
  starters,
  onAsk,
  onStarter,
  onOpenHit,
  onSeeAll,
  onClose,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [activeIndex, setActiveIndex] = useState(0)
  const [trackedKey, setTrackedKey] = useState<string | null>(null)

  const rows = useMemo(
    () => buildPaletteRows({ query, aiEnabled, hits, starters, searching }),
    [query, aiEnabled, hits, starters, searching],
  )
  const blocks = useMemo(() => groupPaletteBlocks(rows), [rows])
  const rowKey = rows.map((row) => row.id).join("|")
  if (trackedKey !== rowKey) {
    setTrackedKey(rowKey)
    setActiveIndex(0)
  }

  const active = rows[activeIndex] ?? null
  const trimmed = query.trim()
  const showSearchStatus = trimmed.length >= PALETTE_MIN_QUERY
  const showNoMatches = showSearchStatus && !searching && !searchError && hits.length === 0

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.nativeEvent.isComposing) return
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, Math.max(rows.length - 1, 0)))
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      }
      if (event.key === "Escape") {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== "Enter" || event.shiftKey) return
      if (!active) return
      event.preventDefault()
      activateRow(active, { onAsk, onStarter, onOpenHit, onSeeAll })
    },
    [rows.length, active, onAsk, onStarter, onOpenHit, onSeeAll, onClose],
  )

  return (
    <div className={classes.wrap}>
      <div className={classes.field} data-ask-composer="">
        <IconSearch size={16} className={classes.askIcon} aria-hidden />
        <input
          className={classes.input}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={rows.length > 0}
          aria-controls={LIST_ID}
          aria-activedescendant={active ? rowDomId(active) : undefined}
          aria-label={aiEnabled ? i18n._(msg`Search or ask`) : i18n._(msg`Search your meetings`)}
          placeholder={aiEnabled ? i18n._(msg`Search or ask…`) : i18n._(msg`Search your meetings…`)}
          value={query}
          onChange={(event) => onQueryChange(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-autofocus
          data-testid="ask-palette-input"
        />
      </div>

      {showSearchStatus && searching ? (
        <p className={classes.status}>
          <Trans>Searching…</Trans>
        </p>
      ) : null}
      {showSearchStatus && searchError ? <p className={classes.status}>{searchError}</p> : null}
      {showNoMatches ? (
        <p className={classes.status}>
          <Trans>No matches.</Trans>
        </p>
      ) : null}
      {!aiEnabled && trimmed.length === 0 ? (
        <p className={classes.status}>
          <Trans>Type at least two letters to search your meetings.</Trans>
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className={classes.list} id={LIST_ID} role="listbox" aria-label={i18n._(msg`Results`)}>
          {blocks.map((block) => (
            <PaletteBlock
              key={blockKey(block)}
              block={block}
              activeIndex={activeIndex}
              query={trimmed}
              onHover={setActiveIndex}
              onChoose={(row) => activateRow(row, { onAsk, onStarter, onOpenHit, onSeeAll })}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function blockKey(block: TPaletteBlock): string {
  if (block.kind === "row") return block.item.row.id
  return `group-${block.channel}-${block.items[0]?.row.id ?? block.channel}`
}

function PaletteBlock({
  block,
  activeIndex,
  query,
  onHover,
  onChoose,
}: {
  block: TPaletteBlock
  activeIndex: number
  query: string
  onHover: (index: number) => void
  onChoose: (row: TPaletteRow) => void
}): React.JSX.Element | null {
  if (block.kind === "row") {
    return (
      <PaletteOption
        row={block.item.row}
        selected={block.item.index === activeIndex}
        query={query}
        onHover={() => onHover(block.item.index)}
        onChoose={() => onChoose(block.item.row)}
      />
    )
  }

  if (block.items.length === 0) return null

  return (
    <div className={classes.group}>
      <div className={classes.groupLabel}>
        <ChannelLabel sourceChannel={block.channel} />
      </div>
      {block.items.map((item) => (
        <PaletteOption
          key={item.row.id}
          row={item.row}
          selected={item.index === activeIndex}
          query={query}
          onHover={() => onHover(item.index)}
          onChoose={() => onChoose(item.row)}
        />
      ))}
    </div>
  )
}

function AskRowLabel({ query }: { query: string }): React.JSX.Element {
  return (
    <span className={classes.askLabel}>
      <IconSparkles size={15} className={classes.askIcon} aria-hidden />
      <Trans>Ask AI about "{query}"</Trans>
    </span>
  )
}

function PaletteOption({
  row,
  selected,
  query,
  onHover,
  onChoose,
}: {
  row: TPaletteRow
  selected: boolean
  query: string
  onHover: () => void
  onChoose: () => void
}): React.JSX.Element {
  const { i18n } = useLingui()

  return (
    <button
      type="button"
      id={rowDomId(row)}
      role="option"
      aria-selected={selected}
      className={classes.option}
      data-active={selected ? "true" : undefined}
      onMouseEnter={onHover}
      onClick={onChoose}
    >
      {row.kind === "ask" ? <AskRowLabel query={row.query} /> : null}
      {row.kind === "starter" ? <span className={classes.askLabel}>{row.label}</span> : null}
      {row.kind === "see-all" ? (
        <span className={classes.askLabel}>
          <Trans>See all results</Trans>
        </span>
      ) : null}
      {row.kind === "hit" ? (
        <HitBody hit={row.hit} query={query} date={formatShortDate(i18n, row.hit.meetingDate)} />
      ) : null}
    </button>
  )
}

function HitBody({
  hit,
  query,
  date,
}: {
  hit: IProps["hits"][number]
  query: string
  date: string
}): React.JSX.Element {
  const { i18n } = useLingui()
  const title = displayMeetingTitle(hit.meetingSubject, null, i18n)
  const ranges = query.length > 0 ? buildHighlightRanges(hit.content, query) : []
  const snippet = windowSnippet(hit.content, ranges)
  const segments = toSegments(snippet.text, snippet.ranges)
  const seekStart = hit.seekStart

  return (
    <>
      <span className={classes.hitHead}>
        <span className={classes.subject}>{title}</span>
        <span className={`${classes.dot} lex-dot`} aria-hidden />
        <time className={classes.date}>{date}</time>
      </span>
      {hit.speaker ? <span className={classes.snippet}>{hit.speaker}</span> : null}
      <span className={classes.snippet}>
        {snippet.leadingEllipsis ? <span aria-hidden>… </span> : null}
        {segments.map((segment, index) =>
          segment.highlight ? (
            <mark key={index} className={classes.mark}>
              {segment.text}
            </mark>
          ) : (
            <span key={index}>{segment.text}</span>
          ),
        )}
      </span>
      {seekStart != null ? (
        <span className={classes.seek}>
          <IconPlayerPlayFilled size={11} aria-hidden />
          <Trans>Play from {formatClock(seekStart)}</Trans>
        </span>
      ) : null}
    </>
  )
}
